import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { ApiService } from 'src/app/core/services/api.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { BelenderWidgetResponse } from 'src/app/models/BelenderWidgetResponse';
import { CancelRequest } from 'src/app/models/CancelRequest';
import { CancelResponse } from 'src/app/models/CancelResponse';
import { FeasibilityData } from 'src/app/models/FeasibilityData';
import { IRPFInformation } from 'src/app/models/IRPFInformation';
import { ODDetailResponse } from 'src/app/models/OpenData.ODDetailResponse';
import { PersonQueryStatusResponse } from 'src/app/models/PersonQueryStatusResponse';
import { ReadJsonRequest } from 'src/app/models/ReadJsonRequest';
import { ReadJsonResponse } from 'src/app/models/ReadJsonResponse';
import { FEASIBILITY_JSON_FILE_NAME } from 'src/app/shared/constants/common.constants';
import { UtilsService } from 'src/app/utils/utils.service';
import { environment } from 'src/environments/environment';
import { IrpfTableService } from '../../../shared/components/irpf-table/irpf-table.service';
import { FreelancerRequestDetailDocumentsService } from './freelancer-request-detail-documents/freelancer-request-detail-documents.service';

@Injectable({
    providedIn: 'root'
})
export class FreelancerRequestDetailService {

    isLoading = false;
    request: ODDetailResponse;
    queryId: string;
    lastQueryHour: Date;
    isHistorySidebarVisible = false;
    isCalculateViabilitySidebarVisible = false;
    feasibilityData: FeasibilityData = {
        isCreditOperationMade: false,
        isCreditGranted: false,
        creditAmount: undefined
    };
    feasibilityEnrichmentData: Record<string, any> | undefined = undefined;

    constructor(
		private readonly apiService: ApiService,
		private readonly router: Router,
		private readonly toastService: ToastService,
        private readonly freelancerRequestDetailDocumentsService: FreelancerRequestDetailDocumentsService,
        private readonly utilsService: UtilsService,
        private readonly irpfTableService: IrpfTableService
    ) { }

    getBelenderWidget(param1: string, param2: string, param3: string): Promise<BelenderWidgetResponse> {
        return this.apiService.get(`requests/belender-widget?param1=${param1}&param2=${param2}&param3=${param3}`);
    }

    getRequestFullPersonName(): string {
        if (this.request != null) {
            return this.request.personQuery?.firstName + ' ' +
				this.request.personQuery?.firstSurname + ' ' +
				(this.request.personQuery.secondSurname ? this.request.personQuery.secondSurname : '');
        }
        return null;
    }

    async saveDocument(url: string, fileName: string): Promise<void> {
        const file = await this.apiService.postBlob(`files/amazon-file`, { url: url, fileName: fileName });
        saveAs(file, fileName);
    }

    saveJSON(json: object, fileName: string): void {
        // Convert the object to a JSON string
        const jsonString = JSON.stringify(json);
        // Create a Blob from the JSON string
        const blob = new Blob([jsonString], { type: 'application/json' });
        saveAs(blob, fileName);
    }

    async getRequestFromUrl(withoutGuard = false): Promise<void> {
        this.queryId = this.validateURLAndGetRequestId(this.router.url);
        if (this.queryId) {
            await this.refreshRequest(withoutGuard);
        } else {
            this.toastService.showError('TOAST.ERROR', 'REQUEST_DETAIL.NO_QUERY_ID', true);
        };
    }

    async refreshRequest(withoutGuard: boolean): Promise<void> {
        try {
            this.isLoading = true;

            await this.setRequest(withoutGuard);

            await Promise.all([
                this.setFeasibilityData(),
                this.setConsentUrlBoxed(),
            ]);

            this.freelancerRequestDetailDocumentsService.setDocumentNames(this.request);
            this.freelancerRequestDetailDocumentsService.expandAll();
        } catch {
            this.toastService.showError('TOAST.ERROR', 'REQUEST_DETAIL.ERROR_NOT_FOUND', true);
        } finally {
            this.isLoading = false;
        }
    }

    private async setConsentUrlBoxed(): Promise<void> {
        if (!this.request.personQuery.consentUrlBoxed) {
            const req = { queryId: this.queryId };
            const res = await this.apiService.post<PersonQueryStatusResponse>('requests/person-query-status', req);
            this.request.personQuery.consentUrlBoxed = res.consentUrlBoxed ? res.consentUrlBoxed : this.utilsService.convertConsentUrlToConsentUrlBoxed(res.consentUrl);
        }
    }

    cancelRequest(cancelRequest: CancelRequest): Promise<CancelResponse> {
        return this.apiService.post<CancelResponse>('requests/delete-request', cancelRequest);
    }

    private validateURLAndGetRequestId(url: string): string | null {
        const regex = /request-detail\/([0-9]{14}-[A-Z0-9]{18})/;
        const matches = url.match(regex);
        if (matches && matches[1]) {
            return matches[1];
        } else {
            return null;
        }
    }

    private async setRequest(withoutGuard: boolean) {
        if (withoutGuard) {
            this.request = await this.apiService.get<ODDetailResponse>(`requests/request-detail?queryId=${this.queryId}`);
        }

        if (!environment.production && !this.request.data['Calculos']) {
            this.request.data['Calculos'] = {};
        }

        await Promise.all([
            this.recalculateODIncomeTemporal(),
            this.recalculateBillingTemporal(),
            this.getIRPFInformation()
        ]);

        this.lastQueryHour = new Date();
    }

    private async recalculateODIncomeTemporal(): Promise<void> {
        if (this.request.data['Calculos']) {
            const odIncome = await this.apiService.post<number>('requests/od-income', this.request);
            this.request.data['Calculos']['OD_Ingresos'] = odIncome;
        }
    }

    private async recalculateBillingTemporal(): Promise<void> {
        if (this.request.data['Calculos']) {
            const billing = await this.apiService.post<number>('requests/billing', this.request);
            this.request.data['Calculos']['Facturacion'] = billing;
        }
    }

    private async getIRPFInformation(): Promise<void> {
        this.irpfTableService.isInit.set(false);
        if (!this.request.data || !Object.keys(this.request.data).some(k => k.startsWith('100'))) return;

        try {
            const response = await this.apiService.post<IRPFInformation>('requests/irpf-information', this.request);
            this.irpfTableService.init(this.queryId, response);
        } catch (error) {
            console.log(error);
        }
    }

    private async setFeasibilityData(): Promise<void> {
        try {
            this.feasibilityEnrichmentData = undefined;
            this.freelancerRequestDetailDocumentsService.clearEnrichmentAccordion();
            if(!this.request.personQuery.isDocumentsDowloadCompleted) return;

            const request: ReadJsonRequest = {
                queryId: this.queryId,
                filename: FEASIBILITY_JSON_FILE_NAME
            };

            this.freelancerRequestDetailDocumentsService.addEnrichmentDataToAccordion(this.request);

            const response = await this.apiService.post<ReadJsonResponse>('requests/read-json', request);
            this.feasibilityEnrichmentData = response.data;
            this.freelancerRequestDetailDocumentsService.addFeasibilityEnrichmentDataToAccordion(this.feasibilityEnrichmentData || {});

            this.feasibilityData = {
                isCreditOperationMade: true,
                isCreditGranted: response.data['Viable'] === true ? true : false,
                creditAmount: Number(response.data['CalculoDelLimite'])
            };
        } catch {
            this.feasibilityData = {
                isCreditOperationMade: false,
                isCreditGranted: false,
                creditAmount: undefined
            };
        }
    }

    resetFeasibilityData(): void {
        this.feasibilityEnrichmentData = undefined;
        this.feasibilityData = {
            isCreditOperationMade: false,
            isCreditGranted: false,
            creditAmount: undefined
        };
    }

    showDocumentDetails(name: string): boolean {
        return !(name === 'Score' && this.request.data['Score'] === null);
    }
}
